import React from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Button from '@components/shared/Button';
import Text from '@components/shared/Text';
import TwoColumn from '@components/shared/TwoColumn';
import { PageSlice } from '@contracts';
import useNavigate from '@utils/useNavigate';
import Colors from './Colors';

interface Props {
  slice: PageSlice;
}

const ImageTextBulletList: React.FC<Props> = ({ slice }) => {
  const { navigateWrapper } = useNavigate();
  const image = getImage(slice.primary?.image);
  const imageAlt = slice.primary?.image.alt ?? '';
  const { alignment, padding } = slice.primary;
  const layout = slice.primary?.layout;
  const desktopReverse = layout === 'text-image';
  const mobileReverse = slice.primary?.mobile_reverse;
  const mobilePadding = slice.primary?.padding_mobile?.text !== '' ? slice.primary?.padding_mobile?.text : padding;
  const horizontalBulletsMobile = slice.primary?.horizontal_mobile_bullets ?? false;
  const gap = slice.primary?.gap?.text;
  const backgorundColor = slice.primary?.background_color;
  const textColor = slice.primary?.main_text_color;
  const textHTML = slice.primary?.text?.html;
  const subtextHTML = slice.primary?.subtext?.html;
  const imageWidth = slice.primary?.image_width?.text || '400px';
  const mobileImageWidth = slice.primary?.mobile_image_width?.text || '300px';
  const textWidth = slice.primary?.text_width?.text || '476px';
  const bullets = slice.items;
  const buttonText = slice.primary?.button_text?.text ?? '';
  const buttonPath = slice.primary?.button_path?.text ?? '';

  const handleNavigation = (buttonPath: string) => {
    navigateWrapper(buttonPath);
  };

  return (
    <Container $padding={padding?.text ?? '30px'} $mobilePadding={mobilePadding} $backgroundColor={backgorundColor}>
      <TwoColumn layout={layout} desktopReverse={desktopReverse} mobileReverse={mobileReverse} alignment={alignment} gap={gap}>
        <ImgWrap $imageWidth={imageWidth} $mobileImageWidth={mobileImageWidth}>
          {image && <GatsbyImage image={image} alt={imageAlt} />}
        </ImgWrap>
        <TextColumn $textColor={textColor} $textWidth={textWidth}>
          <Text text={textHTML} textAlign={slice.primary?.text_align} />
          <BulletsColumn $rowBullets={horizontalBulletsMobile}>
            {bullets &&
              bullets?.map((bullet, index) => (
                <BulletWrap key={index} $rowBullets={horizontalBulletsMobile}>
                  <GatsbyImage
                    image={bullet.bullet_icon.gatsbyImageData}
                    alt={bullet.bullet_icon.alt ?? ''}
                    objectFit="contain"
                    style={{ minWidth: '22px' }}
                  />
                  <BulletText
                    $longText={bullet?.bullet_text?.text}
                    $shortText={bullet?.short_text?.text === '' ? bullet?.bullet_text?.text : bullet?.short_text?.text}
                    $horizontalBulletsMobile={horizontalBulletsMobile}
                  />
                </BulletWrap>
              ))}
          </BulletsColumn>
          {buttonPath && <ButtonStyled onClick={() => handleNavigation(buttonPath)}>{buttonText}</ButtonStyled>}
          <Subtext $textColor={textColor} dangerouslySetInnerHTML={{ __html: subtextHTML }} />
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};

export default ImageTextBulletList;

interface StyleProps {
  $padding?: string;
  $mobilePadding?: string;
  $textColor?: string;
  $backgroundColor?: string;
  $imageWidth?: string;
  $mobileImageWidth?: string;
  $textWidth?: string;
  $rowBullets?: boolean;
}

const Container = styled.div<StyleProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  padding: ${(props) => (props.$padding ? props.$padding : '30px')};
  background-color: ${(props) => (props.$backgroundColor ? props.$backgroundColor : '')};

  @media (max-width: 1024px) {
    p,
    h2,
    h3,
    h4,
    h5,
    h6 {
      text-align: center;
    }
  }

  p {
    font-size: 18px;
    margin-bottom: 20px;
  }

  h2 {
    margin-bottom: 20px;
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.$mobilePadding ? props.$mobilePadding : '30px')};
    border: none;
    margin-left: 0;
    margin-right: 0;

    p {
      margin-bottom: 25px;
    }
  }
`;

const TextColumn = styled.div<StyleProps>`
  max-width: ${(props) => props.$textWidth};
  color: ${(props) => props.$textColor};
  align-items: flex-start;
  display: flex;

  @media (max-width: 1024px) {
    margin-top: 30px;
    align-items: center;
  }
`;

const ImgWrap = styled.div<StyleProps>`
  max-width: ${(props) => props.$imageWidth};

  @media (max-width: 1024px) {
    max-width: ${(props) => props.$mobileImageWidth};
    align-self: center;
  }
`;

const BulletsColumn = styled.div<StyleProps>`
  display: flex;
  gap: 20px;
  flex-direction: column;

  @media (max-width: 1024px) {
    flex-direction: ${(props) => (props.$rowBullets ? 'row' : 'column')};
    padding-bottom: 20px;
  }
`;

const BulletWrap = styled.div<StyleProps>`
  display: flex;
  align-items: center;
  align-self: start;
  gap: 15px;

  @media (max-width: 1024px) {
    flex-direction: ${(props) => (!props.$rowBullets ? 'row' : 'column')};
    margin-left: ${(props) => (props.$rowBullets ? '0' : '15px')};
    align-self: start;
  }
`;

export const BulletText = styled.span<{ $longText: string; $shortText: string; $horizontalBulletsMobile: boolean }>`
  font-family: BrownStd;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  width: 260px;

  &:after {
    content: '${(p) => p.$longText}';
  }

  @media (max-width: 1024px) {
    font-weight: 700;
    font-size: 14px;
    line-height: 140%;
    width: unset;
    max-width: 200px;
    text-align: ${(p) => (p.$horizontalBulletsMobile ? 'center' : 'left')};
    &:after {
      content: '${(p) => p.$shortText}';
    }
  }
`;

const Subtext = styled.div<StyleProps>`
  margin-top: 40px;
  p {
    font-family: BrownStd;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    color: ${(props) => props.$textColor};
    opacity: 0.5;
  }
`;

const ButtonStyled = styled(Button)`
  margin-top: 30px;
  color: ${Colors.cream};
  background: ${Colors.lilac};
`;
